import { useCallback, useEffect, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import { api, type EllenorzesNap, type NapAdat, type NapokValasz } from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { ft, szam } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { EllenorzesSofor } from '../reszek/EllenorzesSofor.tsx';
import {
  elojeles,
  kiadasBontasSzoveg,
  osszesitoElszamolasMentese,
} from '../reszek/elszamolas-dokumentum.ts';

/**
 * Ellenőrzés — a másnapi elszámolás sofőrönként. (Megbízás 6.3)
 *
 * Minden sofőr fülén a **teljes** járatlap látszik. Amelyik sorba beírsz
 * mennyiséget, az a sor onnantól azé a sofőré; a többieknél szürkén, zárolva
 * jelenik meg a tulajdonos nevével. Külön beosztás nincs — a kitöltés maga
 * a kiosztás.
 */
export function Ellenorzes({ kezdoNap }: { kezdoNap?: string | null }) {
  const [napok, setNapok] = useState<NapAdat[]>([]);
  const [statuszNevek, setStatuszNevek] = useState<Record<string, string>>({});
  const [datum, setDatum] = useState<string | null>(kezdoNap ?? null);
  const [ellenorzes, setEllenorzes] = useState<EllenorzesNap | null>(null);
  const [aktivFul, setAktivFul] = useState<number | 'osszesito' | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [toltes, setToltes] = useState(true);

  /* --- csak a kiadott, ellenőrzés alatti és lezárt napok jöhetnek szóba --- */
  const napokBetolt = useCallback(async () => {
    try {
      const v = await api.get<NapokValasz>('/api/jarat/napok?mennyi=20');
      const jeloltek = v.napok.filter((n) => n.statusz !== 'tervezes' && n.cimek > 0);
      setNapok(jeloltek);
      setStatuszNevek(v.statusz_nevek);
      setDatum(
        (d) =>
          d ??
          jeloltek.find((n) => n.statusz !== 'lezarva')?.datum ??
          jeloltek.at(-1)?.datum ??
          null,
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a napokat.');
    } finally {
      setToltes(false);
    }
  }, []);

  useEffect(() => {
    void napokBetolt();
  }, [napokBetolt]);

  const betolt = useCallback(async () => {
    if (!datum) return;
    try {
      // Az első megnyitáskor a nap átbillen „ellenőrzés" állapotba.
      const eredmeny = await api.post<EllenorzesNap>(`/api/ellenorzes/${datum}/megkezdes`);
      setEllenorzes(eredmeny);
      setHiba(null);
      // A nap állapota változhatott (kiadva → ellenőrzés alatt), ezért a
      // választólistát is frissítjük, hogy ne mutasson elavult állapotot.
      setNapok((elozo) =>
        elozo.map((n) => (n.datum === datum ? { ...n, statusz: eredmeny.nap.statusz } : n)),
      );
      setAktivFul((elozo) => {
        if (elozo !== null) return elozo;
        const elsoNyitott = eredmeny.soforok.find((s) => !s.lezarva);
        return elsoNyitott?.sofor_id ?? eredmeny.soforok[0]?.sofor_id ?? 'osszesito';
      });
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a napot.');
    }
  }, [datum]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  if (toltes) return <div className="halvany" style={{ padding: 20 }}>Betöltés…</div>;

  if (napok.length === 0) {
    return (
      <>
        <div className="dayhead">
          <span style={{ fontSize: 15, fontWeight: 600 }}>Ellenőrzés</span>
        </div>
        <div className="blank">
          Még nincs kiadott járatlap. A Járatszervezésen állítsd össze a napot, és add ki —
          onnantól itt lehet elszámolni.
        </div>
      </>
    );
  }

  const aktivSofor =
    typeof aktivFul === 'number'
      ? ellenorzes?.soforok.find((s) => s.sofor_id === aktivFul)
      : undefined;

  return (
    <>
      <div className="dayhead">
        <select value={datum ?? ''} onChange={(e) => setDatum(e.target.value)}>
          {napok.map((n) => (
            <option key={n.datum} value={n.datum}>
              {n.felirat} · {statuszNevek[n.statusz] ?? n.statusz}
            </option>
          ))}
        </select>

        <span className={`allapotjel ${ellenorzes?.nap.statusz ?? ''}`}>
          {statuszNevek[ellenorzes?.nap.statusz ?? ''] ?? ''}
        </span>

        <div className="stats">
          <div>
            Kitöltve
            <strong>
              {(ellenorzes?.sorok.length ?? 0) - (ellenorzes?.kiosztatlan ?? 0)} /{' '}
              {ellenorzes?.sorok.length ?? 0}
            </strong>
          </div>
          <div>
            Liter
            <strong>{szam(ellenorzes?.osszesito?.liter ?? 0, 0)}</strong>
          </div>
          <div>
            Leadó
            <strong>{ft(ellenorzes?.osszesito?.leado ?? 0)}</strong>
          </div>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba.split('\n').map((s, i) => (
            <div key={i}>{s}</div>
          ))}
        </div>
      )}

      {!ellenorzes?.van_sofor && (
        <div className="uzenet flag">
          Még nincs felvéve egyetlen aktív sofőr sem. A Törzsadatok → Sofőrök és autók
          fülön vedd fel őket, mielőtt elszámolnál.
        </div>
      )}

      {/* --- sofőrfülek --- */}
      <div className="drv">
        {(ellenorzes?.soforok ?? []).map((s) => (
          <button
            key={s.sofor_id}
            className={aktivFul === s.sofor_id ? 'on' : ''}
            onClick={() => setAktivFul(s.sofor_id)}
          >
            <span
              className={`dot${s.lezarva ? ' done' : s.megkezdte ? ' act' : ''}`}
            ></span>
            {s.nev}
            <span className="cnt">{s.lezarva ? 'lezárva' : `${s.sorok} cím`}</span>
          </button>
        ))}
        <button
          className={`sumtab${aktivFul === 'osszesito' ? ' on' : ''}`}
          onClick={() => setAktivFul('osszesito')}
        >
          Összesítő ellenőrzés
        </button>
      </div>

      {aktivSofor && datum && ellenorzes && (
        <EllenorzesSofor
          datum={datum}
          allapot={aktivSofor}
          ellenorzes={ellenorzes}
          onFrissit={setEllenorzes}
          onHiba={setHiba}
        />
      )}

      {aktivFul === 'osszesito' && ellenorzes && datum && (
        <Osszesito
          datum={datum}
          ellenorzes={ellenorzes}
          onFrissit={setEllenorzes}
          onHiba={setHiba}
          onAllapotValtozott={(statusz) =>
            setNapok((elozo) =>
              elozo.map((n) => (n.datum === datum ? { ...n, statusz } : n)),
            )
          }
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Összesítő ellenőrzés.
 *
 * **Csak a lezárt sofőrök adatai jelennek meg**; a többinél gondolatjel áll.
 * Így nem lehet félkész számokból következtetni.
 */
function Osszesito({
  datum,
  ellenorzes,
  onFrissit,
  onHiba,
  onAllapotValtozott,
}: {
  datum: string;
  ellenorzes: EllenorzesNap;
  onFrissit: (uj: EllenorzesNap) => void;
  onHiba: (uzenet: string) => void;
  onAllapotValtozott: (statusz: NapAdat['statusz']) => void;
}) {
  const letolthet = useMuvelet('export_jarat');
  const alapadatok = useAlapadatok();
  const o = ellenorzes.osszesito;
  const lezart = ellenorzes.nap.statusz === 'lezarva';
  // Az elszámolási lap minden kitöltött sofőrről készül, lezárástól függetlenül.
  const kitoltottek = ellenorzes.soforok.filter((s) => s.sorok > 0);

  const ertek = (s: (typeof ellenorzes.soforok)[number], mit: () => string) =>
    s.lezarva ? mit() : '—';

  async function hivas(mit: () => Promise<EllenorzesNap>) {
    try {
      const eredmeny = await mit();
      onFrissit(eredmeny);
      onAllapotValtozott(eredmeny.nap.statusz);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A művelet nem sikerült.');
    }
  }

  return (
    <>
      <div className="sheet">
        <table className="sumtab">
          <thead>
            <tr>
              <th>Sofőr</th>
              <th className="n">Cím</th>
              <th className="n">Liter</th>
              <th className="n" title="Az F+ jelölésű sorok literje (a Literben is benne van)">
                F+ liter
              </th>
              <th className="n">Eladás</th>
              <th className="n">Áfa</th>
              <th className="n">Átlagár</th>
              <th className="n">Nem kp.</th>
              <th className="n">Készpénzes</th>
              <th className="n" title="A moslék összege, minden fizetési móddal">
                Moslék
              </th>
              <th className="n" title="A moslékból ami készpénzben folyt be">
                ebből kp.
              </th>
              <th className="n" title="Kanna betétdíj, csak a készpénzes sorokból">
                Kanna (kp.)
              </th>
              <th className="n" title="Hordó betétdíj, csak a készpénzes sorokból">
                Hordó (kp.)
              </th>
              <th className="n" title="A kézzel felvett egyéb beszedés">
                Beszedés
              </th>
              <th className="n">Kiadás</th>
              <th className="n">HSO</th>
              <th className="n" title="A kiadás a HSO nélkül: tankolás, kanna, egyéb">
                Egyéb kiadás
              </th>
              <th className="n" title="Raktári (R) liter">
                R liter
              </th>
              <th className="n" title="A sofőr megszámolt záró készlete">
                Záró
              </th>
              <th className="n">Leadó</th>
              <th>Állapot</th>
            </tr>
          </thead>
          <tbody>
            {ellenorzes.soforok.map((s) => {
              const x = s.osszesites;
              return (
                <tr key={s.sofor_id} className={s.lezarva ? undefined : 'nul'}>
                  <td className="nm">
                    <b>{s.nev}</b>
                  </td>
                  <td className="sum">{s.sorok}</td>
                  <td className="sum">{ertek(s, () => szam(x.liter, 1))}</td>
                  <td className="sum">{ertek(s, () => szam(x.fplusz_liter, 1))}</td>
                  <td className="sum">{ertek(s, () => ft(x.eladas))}</td>
                  <td className="sum">{ertek(s, () => ft(x.afa))}</td>
                  <td className="sum">
                    {ertek(s, () => (x.atlagar === null ? '—' : szam(x.atlagar, 2)))}
                  </td>
                  <td className="sum">{ertek(s, () => ft(x.nem_keszpenzes))}</td>
                  <td className="sum">{ertek(s, () => ft(x.keszpenzes))}</td>
                  <td className="sum">{ertek(s, () => ft(x.mo_osszeg))}</td>
                  <td className="sum">{ertek(s, () => ft(x.mo_keszpenzes))}</td>
                  <td className="sum">{ertek(s, () => elojeles(x.kanna_keszpenzes))}</td>
                  <td className="sum">{ertek(s, () => ft(x.hordo_keszpenzes))}</td>
                  <td className="sum">{ertek(s, () => ft(x.beszedes))}</td>
                  <td className="sum">{ertek(s, () => ft(x.kiadas))}</td>
                  <td className="sum">{ertek(s, () => ft(x.hso_osszeg))}</td>
                  <td className="sum" title={s.lezarva ? kiadasBontasSzoveg(x) : undefined}>
                    {ertek(s, () => ft(x.egyeb_kiadas))}
                  </td>
                  <td className="sum">{ertek(s, () => szam(x.raktari_liter, 1))}</td>
                  <td className="sum">
                    {ertek(s, () =>
                      s.keszlet.megszamolt_zaro === null ? '—' : szam(s.keszlet.megszamolt_zaro, 1),
                    )}
                  </td>
                  <td className="sum">
                    <b>{ertek(s, () => ft(x.leado))}</b>
                  </td>
                  <td>
                    {s.lezarva ? (
                      <span className="only" style={{ color: 'var(--ok)' }}>
                        lezárva
                      </span>
                    ) : s.megkezdte ? (
                      <span className="only">folyamatban</span>
                    ) : (
                      <span className="halvany">nem kezdte el</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {o && (
            <tfoot>
              <tr>
                <td className="lab">Lezártak összesen</td>
                <td></td>
                <td>{szam(o.liter, 1)}</td>
                <td>{szam(o.fplusz_liter, 1)}</td>
                <td>{ft(o.eladas)}</td>
                <td>{ft(o.afa)}</td>
                <td>{o.atlagar === null ? '—' : szam(o.atlagar, 2)}</td>
                <td>{ft(o.nem_keszpenzes)}</td>
                <td>{ft(o.keszpenzes)}</td>
                <td>{ft(o.mo_osszeg)}</td>
                <td>{ft(o.mo_keszpenzes)}</td>
                <td>{elojeles(o.kanna_keszpenzes)}</td>
                <td>{ft(o.hordo_keszpenzes)}</td>
                <td>{ft(o.beszedes)}</td>
                <td>{ft(o.kiadas)}</td>
                <td>{ft(o.hso_osszeg)}</td>
                <td title={kiadasBontasSzoveg(o)}>{ft(o.egyeb_kiadas)}</td>
                <td>{szam(o.raktari_liter, 1)}</td>
                <td>
                  {szam(
                    ellenorzes.soforok
                      .filter((s) => s.lezarva)
                      .reduce((a, s) => a + (s.keszlet.megszamolt_zaro ?? 0), 0),
                    1,
                  )}
                </td>
                <td>{ft(o.leado)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* A tankolások tételesen, rendszámmal — a lezárt sofőrökből, mint a tábla. (2026-09-17) */}
      {o && o.tankolasok.length > 0 && (
        <div className="sheet" style={{ marginTop: 10 }}>
          <p className="lbl" style={{ margin: '0 0 4px' }}>
            Tankolások
          </p>
          <table className="sumtab">
            <thead>
              <tr>
                <th>Sofőr</th>
                <th>Rendszám</th>
                <th>Fizetés</th>
                <th>Megjegyzés</th>
                <th className="n">Összeg</th>
              </tr>
            </thead>
            <tbody>
              {o.tankolasok.map((t) => (
                <tr key={t.penz_tetel_id}>
                  <td className="nm">
                    <b>{t.sofor_nev}</b>
                  </td>
                  <td>{t.rendszam ?? <span className="halvany">nincs megadva</span>}</td>
                  <td>
                    {alapadatok.fizetesi_modok.find((m) => m.kod === t.fizetesi_mod)?.nev ??
                      'Készpénz'}
                  </td>
                  <td>{t.megjegyzes ?? ''}</td>
                  <td className="sum">{ft(t.osszeg)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="lab">Tankolás összesen</td>
                <td></td>
                <td></td>
                <td></td>
                <td>{ft(o.tankolas_osszeg)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="warns">
        {ellenorzes.kiosztatlan > 0 && (
          <div className="warn">
            {ellenorzes.kiosztatlan} cím maradt, amit egyik sofőrnél sem töltöttek ki:{' '}
            {ellenorzes.kiosztatlan_nevek.slice(0, 8).join(', ')}
            {ellenorzes.kiosztatlan_nevek.length > 8
              ? ` és még ${ellenorzes.kiosztatlan_nevek.length - 8}`
              : ''}
            .
          </div>
        )}
        {!lezart &&
          ellenorzes.nap_akadalyok.map((a) => (
            <div className="warn" key={a}>
              {a}
            </div>
          ))}
      </div>

      <div className="actbar">
        {lezart ? (
          <>
            <button
              className="btn"
              onClick={() =>
                void hivas(async () => {
                  try {
                    return await api.post<EllenorzesNap>(`/api/ellenorzes/${datum}/visszanyit`);
                  } catch (h) {
                    // Lezárt év napja: megerősítés után újra. (Évzárás, 2026-09-17)
                    const uzenet = h instanceof Error ? h.message : '';
                    if (!uzenet.startsWith('ÉVZÁRÁS:')) throw h;
                    const igen = window.confirm(
                      `${uzenet.slice('ÉVZÁRÁS:'.length).trim()}\n\nBiztosan visszanyitod ezt a napot?`,
                    );
                    if (!igen) throw new Error('A nap zárva maradt.');
                    return api.post<EllenorzesNap>(`/api/ellenorzes/${datum}/visszanyit`, {
                      megerosit: true,
                    });
                  }
                })
              }
            >
              Nap visszanyitása
            </button>
            <span className="hint">
              Ez a nap le van zárva. A módosítás naplózódik.
            </span>
          </>
        ) : (
          <>
            <button
              className="btn pri"
              disabled={!ellenorzes.nap_lezarhato}
              title={
                ellenorzes.nap_lezarhato ? undefined : ellenorzes.nap_akadalyok.join('\n')
              }
              onClick={() =>
                void hivas(() => api.post<EllenorzesNap>(`/api/ellenorzes/${datum}/lezar`))
              }
            >
              Nap lezárása
            </button>
            <span className="hint">
              A nap akkor zárható, ha minden megkezdett sofőr le van zárva. Lezárás után az
              adat megjelenik a kimutatásokban.
            </span>
          </>
        )}
        <span style={{ flex: 1 }}></span>
        <button
          className="btn sm"
          disabled={kitoltottek.length === 0}
          title="A nap összesített elszámolási lapja — a sofőri lap formájában, minden kitöltött sofőr összesen. Külön ablakban, onnan PDF-be is menthető."
          onClick={() =>
            void osszesitoElszamolasMentese({
              datum,
              ellenorzes,
              alapadatok,
            }).catch((h: unknown) =>
              onHiba(h instanceof Error ? h.message : 'Az elszámolási lap nem készült el.'),
            )
          }
        >
          Elszámolás mentése
        </button>
        {letolthet && (
          <button
            className="btn sm"
            onClick={() => letolt(`/api/export/elszamolas/${datum}`)}
            title="A napi elszámolás sofőrönként, Excelben"
          >
            Excel
          </button>
        )}
      </div>
    </>
  );
}
